import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from '../../services/toastService'
import { adminAPI, entityAPI } from '../../services/api'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import { useSightingDetailActions } from '../../hooks/admin/useSightingDetailActions'
import EntityDetailMap from '../EntityDetailMap'
import { createLogger } from '../../utils/logger'
import AdminHeader from '../../components/admin/AdminHeader'
import SightingDetailHeader from './components/SightingDetailHeader'
import SightingForm from './components/SightingForm'
import SightingImageGallery from './components/SightingImageGallery'
import SightingDetailsPanel from './components/SightingDetailsPanel'
import SightingMetadata from './components/SightingMetadata'

const logger = createLogger('AdminSightingDetail')

export default function SightingDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const isEditMode = id && id !== 'new'
  const { loading, execute } = useAsyncOperation()
  const [sighting, setSighting] = useState(null)
  const [entity, setEntity] = useState(null)
  const [imageActionLoading, setImageActionLoading] = useState(null)
  const [uploadingImages, setUploadingImages] = useState(false)
  
  // Create form state
  const [formData, setFormData] = useState({
    entityId: '',
    latitude: null,
    longitude: null,
    address: '',
    notes: '',
    imageUrl: ''
  })
  const [entitySearch, setEntitySearch] = useState('')
  const [entitySearchResults, setEntitySearchResults] = useState([])
  const [selectedEntity, setSelectedEntity] = useState(null)
  const [showEntitySearch, setShowEntitySearch] = useState(false)
  const [imagePreview, setImagePreview] = useState(null)
  const [uploadingImage, setUploadingImage] = useState(false)
  const [mapPosition, setMapPosition] = useState(null)

  useEffect(() => {
    if (isEditMode) {
      loadSighting()
    }
  }, [id])

  const loadSighting = async () => {
    await execute(
      async () => {
        const response = await adminAPI.getSighting(id)
        const sightingData = response.data
        setSighting(sightingData)
        
        if (sightingData.entityId) {
          try {
            const entityResponse = await entityAPI.getEntity(sightingData.entityId)
            setEntity(entityResponse.data)
          } catch (error) {
            logger.error('Failed to load entity details', error)
          }
        }
        return sightingData
      },
      { errorMessage: 'Failed to load sighting details' }
    )
  }

  // Extract all handlers to custom hook
  const {
    handleEntitySearch,
    handleSelectEntity,
    handleClearEntity,
    handleMapPositionChange,
    handleImageSelect,
    handleRemoveImage,
    handleSubmitSighting,
    handleApproveImage,
    handleSetPrimaryImage,
    handleModerateSighting,
    handleDeleteImage,
    handleImageUpload,
    handleDeleteSighting
  } = useSightingDetailActions({
    sightingId: id,
    loadSighting,
    execute,
    formData,
    setFormData,
    setMapPosition,
    setImagePreview,
    setUploadingImage,
    setImageActionLoading,
    setUploadingImages,
    setEntitySearch,
    setEntitySearchResults,
    setSelectedEntity,
    setShowEntitySearch
  })

  // Loading state
  if (loading && isEditMode && !sighting) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">{t('app.loading')}</div>
      </div>
    )
  }

  // Not found state
  if (!sighting && isEditMode) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Sighting not found</p>
          <button
            onClick={() => navigate('/admin/sightings')}
            className="text-primary hover:text-primary-600"
          >
            {t('admin.backToCollection')}
          </button>
        </div>
      </div>
    )
  }

  // Create mode
  if (!isEditMode) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <SightingDetailHeader
            isEditMode={false}
            sighting={null}
            loading={loading}
          />
          
          <div className="bg-white rounded-lg shadow-md p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Create New Sighting</h2>
            <p className="text-gray-600 mb-8">
              Create a new sighting record for an entity location.
            </p>

            <SightingForm
              formData={formData}
              onChange={setFormData}
              onSubmit={handleSubmitSighting}
              entitySearch={entitySearch}
              onEntitySearchChange={handleEntitySearch}
              entitySearchResults={entitySearchResults}
              showEntitySearch={showEntitySearch}
              onShowEntitySearchChange={setShowEntitySearch}
              selectedEntity={selectedEntity}
              onSelectEntity={handleSelectEntity}
              onClearEntity={handleClearEntity}
              mapPosition={mapPosition}
              onMapPositionChange={handleMapPositionChange}
              imagePreview={imagePreview}
              onImageSelect={handleImageSelect}
              onRemoveImage={handleRemoveImage}
              loading={loading}
              uploadingImage={uploadingImage}
            />
          </div>
        </div>
      </div>
    )
  }

  // Edit/View mode
  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader />
      <div className="py-8">
        <div className="max-w-4xl mx-auto px-4">
          <SightingDetailHeader
            isEditMode={true}
            sighting={sighting}
          onApprove={() => handleModerateSighting('approve')}
          onReject={() => handleModerateSighting('reject')}
          onDelete={handleDeleteSighting}
          loading={loading}
        />

        <SightingImageGallery
          sighting={sighting}
          uploadingImages={uploadingImages}
          imageActionLoading={imageActionLoading}
          onImageUpload={handleImageUpload}
          onApproveImage={handleApproveImage}
          onSetPrimaryImage={handleSetPrimaryImage}
          onDeleteImage={handleDeleteImage}
        />

        <SightingDetailsPanel
          sighting={sighting}
          entity={entity}
        />

        <div className="mb-6">
          <EntityDetailMap 
            entityId={sighting.entityId}
            entityName={sighting.entityName}
            entityDetails={entity}
            entityLocation={{
              latitude: sighting.latitude,
              longitude: sighting.longitude
            }}
          />
        </div>

        <SightingMetadata sighting={sighting} />
        </div>
      </div>
    </div>
  )
}
